// Components==============
import Button from 'components/UI/Button/Button';
import RichtTextEditor from 'components/UI/RichTextEditor/RichTextEditor';
import File from 'components/UI/TrainingContent/File/File';
import Image from 'components/UI/TrainingContent/Image/Image';
import Video from 'components/UI/TrainingContent/Video/Video';
import { useTranslate } from 'hooks/useTranslate';
import {
  LessonContentType,
  LessonType,
  TrainingBuilderContext,
} from 'pages/Workspace/TrainingBuilder/TrainingBuilder';
import React, { createContext, useContext, useRef, useState } from 'react';
import { TranslateType } from 'typings';
import './Content.module.scss';

type TrainingContentContextType = {
  content: LessonContentType;
  lesson: LessonType;
  data: any;
};

export const TrainingContentContext = createContext(
  {} as TrainingContentContextType
);

const VideoLink = () => {
  const { data, lesson, content } = useContext(TrainingContentContext);
  const { setSessions, currentSession } = useContext(TrainingBuilderContext);

  const [value, setValue] = useState(data?.value);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    let url = e.target.value;
    setValue(url);

    switch (true) {
      case url.includes('youtube'):
        url = url.replace('watch?v=', 'embed/');
        break;

      case url.includes('loom'):
        url = url.replace('share', 'embed');
        break;

      case url.includes('vimeo'):
        url = url.replace('vimeo.com', 'player.vimeo.com/video');
        break;

      default:
        return;
    }

    setSessions((prev) =>
      prev.map((s) =>
        s.id === currentSession
          ? {
              ...s,
              lessons: s.lessons?.map((l) =>
                l.id === lesson.id
                  ? {
                      ...l,
                      contents: l.contents?.map((c) =>
                        c.id === content.id
                          ? {
                              ...c,
                              block_data: JSON.stringify({
                                value: e.target.value,
                              }),
                              changed: true,
                            }
                          : c
                      ),
                    }
                  : l
              ),
            }
          : s
      )
    );
  }

  return (
    <input
      type="text"
      onChange={handleChange}
      styleName="video-link"
      value={value}
      placeholder="https://youtube/embed"
    />
  );
};

const FileUploadButton = ({ text }: { text: TranslateType }) => {
  const { handleLessonContentFileUpload } = useContext(TrainingBuilderContext);
  const { content, lesson } = useContext(TrainingContentContext);

  const labelRef = useRef<HTMLLabelElement>(null);

  return (
    <>
      <input
        style={{ display: 'none' }}
        type="file"
        id={`btn-${content.id}`}
        onChange={(e) =>
          handleLessonContentFileUpload({
            e,
            contentId: content.id,
            lessonId: lesson.id,
          })
        }
        name="upload"
      />
      <label htmlFor={`btn-${content.id}`} ref={labelRef}>
        <Button
          color="white"
          onClick={() =>
            labelRef.current ? labelRef.current.click() : undefined
          }
        >
          {text}
        </Button>
      </label>
    </>
  );
};

export default function Content({
  content,
  lesson,
}: {
  content: LessonContentType;
  lesson: LessonType;
}) {
  const data =
    typeof content.block_data === 'string'
      ? JSON.parse(content.block_data)
      : content.block_local_data
      ? JSON.parse(content.block_local_data)
      : undefined;

  const { currentSession, setSessions } = useContext(TrainingBuilderContext);

  const translate = useTranslate();

  function handleTextChange(val: string) {
    setSessions((prev) =>
      prev.map((s) =>
        s.id === currentSession
          ? {
              ...s,
              lessons: s.lessons?.map((l) =>
                l.id === lesson.id
                  ? {
                      ...l,
                      contents: l.contents?.map((c) =>
                        c.id === content.id
                          ? {
                              ...c,
                              block_data: JSON.stringify({
                                value: val,
                              }),
                              changed: true,
                            }
                          : c
                      ),
                    }
                  : l
              ),
            }
          : s
      )
    );
  }

  return (
    <TrainingContentContext.Provider value={{ content, lesson, data }}>
      {content.block_type === 'file' && (
        <>
          {data.file_path ? (
            <File editMode name={data.file_name} path={data.file_path} />
          ) : (
            <FileUploadButton
              text={translate('general.terms.upload-document')}
            />
          )}
        </>
      )}
      {content.block_type === 'image' && (
        <>
          {data.file_path ? (
            <Image
              path={data.file_path?.replace('public', '/storage')}
              name={data.file_name}
            />
          ) : (
            <FileUploadButton text={translate('general.terms.upload-photo')} />
          )}
        </>
      )}
      {content.block_type === 'text' && (
        <RichtTextEditor
          initial={data?.value}
          onChange={(val) => handleTextChange(val)}
        />
      )}
      {content.block_type === 'video' && (
        <> {data.value ? <Video link={data.value} /> : <VideoLink />}</>
      )}
    </TrainingContentContext.Provider>
  );
}
